import React, { useState, useEffect } from 'react'
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaSave,
  FaSearch,
  FaCar,
  FaHotel,
  FaUser,
  FaPercent,
  FaTags,
  FaChartBar,
  FaEnvelope,
  FaReply,
  FaCheckCircle,
  FaCalendarCheck,
  FaBan,
  FaCog,
  FaStar,
  FaComments,
  FaPalette,
  FaImage,
  FaRoute,
  FaUsers,
  FaDownload,
  FaMapMarkerAlt,
  FaBug,
  FaExternalLinkAlt,
  FaShieldAlt,
} from 'react-icons/fa'
import adminService from '../services/adminService'
import tourService from '../services/tourService'
import serviceService from '../services/serviceService'
import discountService from '../services/discountService'
import contactService from '../services/contactService'
import bookingService from '../services/bookingService'
import settingsService from '../services/settingsService'
import reviewService from '../services/reviewService'
import bugReportService from '../services/bugReportService'
import Loader from '../components/Loader'
import PriceBreakup from '../components/PriceBreakup'
import ThemeSettings from '../components/ThemeSettings'
import HeroSliderSettings from '../components/HeroSliderSettings'
import CustomTripRequests from '../components/CustomTripRequests'
import AboutSettings from '../components/AboutSettings'
import PolicySettings from '../components/PolicySettings'
import LocationManager from '../components/LocationManager'
import EmailTemplateSettings from '../components/EmailTemplateSettings'
import BugReportForm from '../components/BugReportForm'
import Button from '../components/ui/Button'
import useActivityTracker from '../useActivityTracker'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { formatDate, formatCurrency, getServiceImage } from '../utils/helpers'
import SEO from '../utils/SEO'

const generateVoucher = (booking) => {
  const voucherHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Booking Voucher #${booking.id}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; background: #f5f5f5; padding: 20px; }
        .voucher { max-width: 800px; margin: 0 auto; background: white; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
        .header { background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; padding: 30px 40px; }
        .header h1 { font-size: 28px; margin-bottom: 4px; }
        .header p { opacity: 0.9; font-size: 14px; }
        .badge { display: inline-block; background: rgba(255,255,255,0.2); padding: 6px 16px; border-radius: 20px; margin-top: 10px; font-weight: 600; }
        .body { padding: 40px; }
        .section { margin-bottom: 24px; }
        .section-title { font-size: 14px; text-transform: uppercase; letter-spacing: 1px; color: #6b7280; margin-bottom: 12px; font-weight: 600; }
        .detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .detail-item { padding: 12px 16px; background: #f9fafb; border-radius: 8px; }
        .detail-item .label { font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
        .detail-item .value { font-size: 16px; font-weight: 600; color: #111827; margin-top: 4px; }
        .price-section { background: #f0fdf4; padding: 20px; border-radius: 8px; border: 1px solid #bbf7d0; }
        .price-row { display: flex; justify-content: space-between; padding: 8px 0; }
        .price-total { font-size: 22px; font-weight: 700; color: #059669; border-top: 2px solid #10b981; padding-top: 12px; margin-top: 8px; }
        .footer { text-align: center; padding: 20px 40px 30px; color: #9ca3af; font-size: 12px; border-top: 1px solid #e5e7eb; }
        @media print { body { padding: 0; background: white; } .voucher { box-shadow: none; } .no-print { display: none; } }
      </style>
    </head>
    <body>
      <div class="voucher">
        <div class="header">
          <h1>TRVL Booking</h1>
          <p>Travel Voucher</p>
          <div class="badge">CONFIRMED</div>
        </div>
        <div class="body">
          <div class="section">
            <div class="section-title">Booking Information</div>
            <div class="detail-grid">
              <div class="detail-item">
                <div class="label">Booking ID</div>
                <div class="value">#${booking.id}</div>
              </div>
              <div class="detail-item">
                <div class="label">Status</div>
                <div class="value" style="color: #059669;">Confirmed</div>
              </div>
              <div class="detail-item">
                <div class="label">Customer</div>
                <div class="value">${booking.user_name || booking.customer_name || 'N/A'}</div>
              </div>
              <div class="detail-item">
                <div class="label">Booking Type</div>
                <div class="value">${(booking.booking_type || 'tour').charAt(0).toUpperCase() + (booking.booking_type || 'tour').slice(1)}</div>
              </div>
            </div>
          </div>
          <div class="section">
            <div class="section-title">Trip Details</div>
            <div class="detail-grid">
              <div class="detail-item">
                <div class="label">Start Date</div>
                <div class="value">${booking.start_date ? new Date(booking.start_date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</div>
              </div>
              <div class="detail-item">
                <div class="label">End Date</div>
                <div class="value">${booking.end_date ? new Date(booking.end_date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</div>
              </div>
              <div class="detail-item">
                <div class="label">Duration</div>
                <div class="value">${booking.number_of_days || 'N/A'} Days</div>
              </div>
              <div class="detail-item">
                <div class="label">Persons</div>
                <div class="value">${booking.number_of_persons || 'N/A'}</div>
              </div>
            </div>
          </div>
          ${
            booking.special_requests
              ? `
          <div class="section">
            <div class="section-title">Special Requests</div>
            <div class="detail-item">
              <div class="value" style="font-weight: normal;">${booking.special_requests}</div>
            </div>
          </div>
          `
              : ''
          }
          <div class="section">
            <div class="section-title">Payment Summary</div>
            <div class="price-section">
              <div class="price-row"><span>Subtotal</span><span>&#8377;${Number(booking.total_amount || 0).toLocaleString('en-IN')}</span></div>
              ${Number(booking.discount_amount) > 0 ? `<div class="price-row" style="color: #059669;"><span>Discount</span><span>-&#8377;${Number(booking.discount_amount).toLocaleString('en-IN')}</span></div>` : ''}
              ${Number(booking.admin_discount) > 0 ? `<div class="price-row" style="color: #7c3aed;"><span>Admin Discount</span><span>-&#8377;${Number(booking.admin_discount).toLocaleString('en-IN')}</span></div>` : ''}
              <div class="price-row price-total"><span>Total Amount</span><span>&#8377;${Number(booking.final_amount || booking.total_amount || 0).toLocaleString('en-IN')}</span></div>
            </div>
          </div>
        </div>
        <div class="footer">
          <p>This is a computer-generated voucher and does not require a signature.</p>
          <p style="margin-top: 4px;">&copy; ${new Date().getFullYear()} TRVL Booking. All rights reserved.</p>
        </div>
      </div>
      <div class="no-print" style="text-align: center; margin-top: 20px;">
        <button onclick="window.print()" style="background: #10b981; color: white; border: none; padding: 12px 32px; border-radius: 8px; font-size: 16px; cursor: pointer;">
          Print / Save as PDF
        </button>
      </div>
    </body>
    </html>
  `
  const blob = new Blob([voucherHTML], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
}

const AdminDashboard = () => {
  const { getActivityLog, getEnvironment } = useActivityTracker()
  const [activeTab, setActiveTab] = useState('overview')
  const [tours, setTours] = useState([])
  const [services, setServices] = useState([])
  const [discounts, setDiscounts] = useState([])
  const [offers, setOffers] = useState([])
  const [queries, setQueries] = useState([])
  const [queryStats, setQueryStats] = useState({})
  const [bookings, setBookings] = useState([])
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [bookingFilter, setBookingFilter] = useState('all')
  const [siteSettings, setSiteSettings] = useState([])
  const [settingsFilter, setSettingsFilter] = useState('all')
  const [reviews, setReviews] = useState([])
  const [reviewFilter, setReviewFilter] = useState('all')
  const [reviewTypeFilter, setReviewTypeFilter] = useState('all')
  const [bugReports, setBugReports] = useState([])
  const [bugStats, setBugStats] = useState({ open: 0, closed: 0, total: 0, critical: 0 })
  const [bugFilter, setBugFilter] = useState('all')
  const [bugSeverityFilter, setBugSeverityFilter] = useState('all')
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState('')
  const [editingItem, setEditingItem] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [serviceFilter, setServiceFilter] = useState('all')
  const [queryFilter, setQueryFilter] = useState('all')
  const [queryTypeFilter, setQueryTypeFilter] = useState('all')
  const [replyText, setReplyText] = useState('')
  const [adminDiscountForm, setAdminDiscountForm] = useState({
    discount_type: 'percentage',
    discount_value: '',
    notes: '',
  })

  // Form states
  const [tourForm, setTourForm] = useState({
    title: '',
    description: '',
    destination: '',
    duration: '',
    base_price: '',
    max_group_size: '',
    difficulty: 'moderate',
    category: 'standard',
    is_featured: false,
    is_active: true,
    images: ['', ''],
    itinerary: '',
    location_id: '',
    included_services: [''],
    available_activities: [''],
  })

  const [serviceForm, setServiceForm] = useState({
    name: '',
    type: 'car',
    description: '',
    detailed_description: '',
    base_price: '',
    package_price: '',
    price_unit: 'per_day',
    capacity: '',
    location_id: '',
    amenities: [''],
    images: [''],
    is_active: true,
    image_url: '',
  })

  const [discountForm, setDiscountForm] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_booking_amount: '',
    max_discount_amount: '',
    applicable_to: 'all',
    start_date: '',
    end_date: '',
    is_active: true,
    usage_limit: '',
  })

  const [offerForm, setOfferForm] = useState({
    title: '',
    description: '',
    offer_type: 'discount',
    tour_id: '',
    discount_percentage: '',
    valid_from: '',
    valid_to: '',
    is_active: true,
    terms_conditions: '',
  })

  useEffect(() => {
    loadData()
  }, [activeTab, queryFilter, queryTypeFilter, bugFilter, bugSeverityFilter])

  const loadData = async () => {
    setLoading(true)
    try {
      switch (activeTab) {
        case 'tours':
          const toursData = await adminService.getAllToursAdmin()
          setTours(toursData.tours)
          break
        case 'services':
          const servicesData = await serviceService.getAllServices()
          setServices(servicesData.services)
          break
        case 'discounts':
          const discountsData = await discountService.getAllDiscounts()
          setDiscounts(discountsData.discounts)
          break
        case 'offers':
          const offersData = await discountService.getActiveOffers()
          setOffers(offersData.offers)
          break
        case 'queries':
          const queriesData = await contactService.getAllQueries({
            status: queryFilter !== 'all' ? queryFilter : undefined,
            query_type: queryTypeFilter !== 'all' ? queryTypeFilter : undefined,
          })
          setQueries(queriesData.queries)
          const statsData = await contactService.getQueryStats()
          setQueryStats(statsData.stats)
          break
        case 'bookings':
          const bookingsData = await adminService.getAllBookings()
          setBookings(bookingsData.bookings)
          break
        case 'reviews':
          const reviewsData = await reviewService.getAllReviews({
            status: reviewFilter !== 'all' ? reviewFilter : undefined,
            type: reviewTypeFilter !== 'all' ? reviewTypeFilter : undefined,
          })
          setReviews(reviewsData.reviews)
          break
        case 'settings':
          const settingsData = await settingsService.getAllSettings(
            settingsFilter !== 'all' ? settingsFilter : null,
          )
          setSiteSettings(settingsData.settings)
          break
        case 'bugs':
          try {
            const [bugsData, bugStatsData] = await Promise.all([
              bugReportService.list({
                status: bugFilter !== 'all' ? bugFilter : undefined,
                severity: bugSeverityFilter !== 'all' ? bugSeverityFilter : undefined,
              }),
              bugReportService.getStats(),
            ])
            setBugReports(bugsData.reports || [])
            const s = bugStatsData.stats || {}
            setBugStats({
              open: s.open || 0,
              closed: s.closed || 0,
              total: s.total || 0,
              critical: s.bySeverity?.critical || 0,
            })
          } catch {
            setBugReports([])
            setBugStats({ open: 0, closed: 0, total: 0, critical: 0 })
          }
          break
        default:
          break
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const fetchReviews = async () => {
    try {
      const data = await reviewService.getAllReviews({
        status: reviewFilter !== 'all' ? reviewFilter : undefined,
        type: reviewTypeFilter !== 'all' ? reviewTypeFilter : undefined,
      })
      setReviews(data.reviews)
    } catch (error) {
      console.error('Error fetching reviews:', error)
      toast.error('Failed to load reviews')
    }
  }

  // ============ TOURS FUNCTIONS ============
  const handleCreateTour = (e) => {
    if (e && e.preventDefault) { e.preventDefault(); e.stopPropagation() }
    setModalType('tour')
    setEditingItem(null)
    setTourForm({
      title: '',
      description: '',
      destination: '',
      duration: '',
      base_price: '',
      max_group_size: '',
      difficulty: 'moderate',
      category: 'standard',
      is_featured: false,
      is_active: true,
      images: ['', ''],
      itinerary: '',
      location_id: '',
      included_services: [''],
      available_activities: [''],
    })
    setShowModal(true)
  }

  const handleEditTour = (tour) => {
    setModalType('tour')
    setEditingItem(tour)
    const tourImages = typeof tour.images === 'string' ? JSON.parse(tour.images) : tour.images
    const tourItinerary = tour.itinerary
      ? typeof tour.itinerary === 'string'
        ? tour.itinerary
        : JSON.stringify(tour.itinerary, null, 2)
      : ''
    const parsedServices = tour.included_services
      ? typeof tour.included_services === 'string'
        ? JSON.parse(tour.included_services)
        : tour.included_services
      : []
    const parsedActivities = tour.available_activities
      ? typeof tour.available_activities === 'string'
        ? JSON.parse(tour.available_activities)
        : tour.available_activities
      : []
    setTourForm({
      title: tour.title,
      description: tour.description,
      destination: tour.destination,
      duration: tour.duration,
      base_price: tour.base_price,
      max_group_size: tour.max_group_size,
      difficulty: tour.difficulty,
      category: tour.category,
      is_featured: tour.is_featured || false,
      is_active: tour.is_active !== false,
      images: tourImages || ['', ''],
      itinerary: tourItinerary,
      location_id: tour.location_id || '',
      included_services: parsedServices.length > 0 ? parsedServices : [''],
      available_activities: parsedActivities.length > 0 ? parsedActivities : [''],
    })
    setShowModal(true)
  }

  const handleSaveTour = async () => {
    try {
      // Parse and validate itinerary JSON
      let itineraryData = null
      if (tourForm.itinerary && tourForm.itinerary.trim()) {
        try {
          itineraryData = JSON.parse(tourForm.itinerary)
          if (!Array.isArray(itineraryData)) {
            toast.error('Itinerary must be a JSON array')
            return
          }
        } catch (err) {
          toast.error('Invalid itinerary JSON format')
          return
        }
      }

      const tourData = {
        ...tourForm,
        duration: tourForm.duration === '' ? undefined : Number(tourForm.duration),
        base_price: tourForm.base_price === '' ? undefined : Number(tourForm.base_price),
        max_group_size:
          tourForm.max_group_size === '' ? undefined : Number(tourForm.max_group_size),
        location_id: tourForm.location_id || null,
        images: JSON.stringify(tourForm.images.filter((img) => img.trim())),
        itinerary: itineraryData ? JSON.stringify(itineraryData) : null,
        included_services: JSON.stringify(tourForm.included_services.filter((s) => s.trim())),
        available_activities: JSON.stringify(tourForm.available_activities.filter((a) => a.trim())),
      }

      if (editingItem) {
        await adminService.updateTour(editingItem.id, tourData)
        toast.success('Tour updated successfully')
      } else {
        await adminService.createTour(tourData)
        toast.success('Tour created successfully')
      }

      setShowModal(false)
      loadData()
    } catch (error) {
      console.error('Error saving tour:', error)
      const msg = error.response?.data?.message || 'Failed to save tour'
      toast.error(msg)
    }
  }

  const handleDeleteTour = async (id) => {
    if (window.confirm('Are you sure you want to delete this tour?')) {
      try {
        await adminService.deleteTour(id)
        toast.success('Tour deleted successfully')
        loadData()
      } catch (error) {
        console.error('Error deleting tour:', error)
        toast.error('Failed to delete tour')
      }
    }
  }

  // ============ SERVICES FUNCTIONS ============
  const handleCreateService = () => {
    setModalType('service')
    setEditingItem(null)
    setServiceForm({
      name: '',
      type: 'car',
      description: '',
      detailed_description: '',
      base_price: '',
      package_price: '',
      price_unit: 'per_day',
      capacity: '',
      location_id: '',
      amenities: [''],
      images: [''],
      is_active: true,
      image_url: '',
    })
    setShowModal(true)
  }

  const handleEditService = (service) => {
    setModalType('service')
    setEditingItem(service)
    const serviceAmenities =
      typeof service.amenities === 'string' ? JSON.parse(service.amenities) : service.amenities
    const serviceImages =
      typeof service.images === 'string' ? JSON.parse(service.images) : service.images
    const fallbackImages = service.image_url ? [service.image_url] : ['']
    setServiceForm({
      name: service.name,
      type: service.type,
      description: service.description,
      detailed_description: service.detailed_description || '',
      base_price: service.base_price,
      package_price: service.package_price || '',
      price_unit: service.price_unit,
      capacity: service.capacity,
      location_id: service.location_id || '',
      amenities: serviceAmenities || [''],
      images: serviceImages && serviceImages.length > 0 ? serviceImages : fallbackImages,
      is_active: service.is_active !== false,
      image_url: service.image_url || '',
    })
    setShowModal(true)
  }

  const handleSaveService = async () => {
    try {
      const cleanedImages = serviceForm.images?.filter((img) => img?.trim()) || []
      const serviceData = {
        ...serviceForm,
        amenities: serviceForm.amenities.filter((a) => a.trim()),
        images: cleanedImages,
        image_url: serviceForm.image_url || cleanedImages[0] || null,
        package_price: serviceForm.package_price || null,
        location_id: serviceForm.location_id || null,
      }

      if (editingItem) {
        await adminService.updateService(editingItem.id, serviceData)
        toast.success('Service updated successfully')
      } else {
        await adminService.createService(serviceData)
        toast.success('Service created successfully')
      }

      setShowModal(false)
      loadData()
    } catch (error) {
      console.error('Error saving service:', error)
      const message = error.response?.data?.message || error.message || 'Failed to save service'
      toast.error(message)
    }
  }

  const handleDeleteService = async (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await adminService.deleteService(id)
        toast.success('Service deleted successfully')
        loadData()
      } catch (error) {
        console.error('Error deleting service:', error)
        toast.error('Failed to delete service')
      }
    }
  }

  // ============ DISCOUNTS FUNCTIONS ============
  const handleCreateDiscount = () => {
    setModalType('discount')
    setEditingItem(null)
    setDiscountForm({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      min_booking_amount: '',
      max_discount_amount: '',
      applicable_to: 'all',
      start_date: '',
      end_date: '',
      is_active: true,
      usage_limit: '',
    })
    setShowModal(true)
  }

  const handleEditDiscount = (discount) => {
    setModalType('discount')
    setEditingItem(discount)
    setDiscountForm({
      code: discount.code,
      description: discount.description,
      discount_type: discount.discount_type,
      discount_value: discount.discount_value,
      min_booking_amount: discount.min_booking_amount || '',
      max_discount_amount: discount.max_discount_amount || '',
      applicable_to: discount.applicable_to,
      start_date: discount.start_date || '',
      end_date: discount.end_date || '',
      is_active: discount.is_active !== false,
      usage_limit: discount.usage_limit || '',
    })
    setShowModal(true)
  }

  const handleSaveDiscount = async () => {
    if (!discountForm.code.trim()) {
      toast.error('Discount code is required')
      return
    }
    if (!discountForm.discount_value || discountForm.discount_value === '') {
      toast.error('Discount value is required')
      return
    }
    if (
      discountForm.discount_type === 'percentage' &&
      (parseFloat(discountForm.discount_value) <= 0 ||
        parseFloat(discountForm.discount_value) > 100)
    ) {
      toast.error('Percentage discount must be between 1 and 100')
      return
    }
    try {
      const discountData = { ...discountForm }

      if (editingItem) {
        await adminService.updateDiscount(editingItem.id, discountData)
        toast.success('Discount updated successfully')
      } else {
        await adminService.createDiscount(discountData)
        toast.success('Discount created successfully')
      }

      setShowModal(false)
      loadData()
    } catch (error) {
      console.error('Error saving discount:', error)
      const msg = error.response?.data?.message || 'Failed to save discount'
      toast.error(msg)
    }
  }

  const handleDeleteDiscount = async (id) => {
    if (window.confirm('Are you sure you want to delete this discount?')) {
      try {
        await adminService.deleteDiscount(id)
        toast.success('Discount deleted successfully')
        loadData()
      } catch (error) {
        console.error('Error deleting discount:', error)
        toast.error('Failed to delete discount')
      }
    }
  }

  // ============ OFFERS FUNCTIONS ============
  const handleCreateOffer = () => {
    setModalType('offer')
    setEditingItem(null)
    setOfferForm({
      title: '',
      description: '',
      offer_type: 'discount',
      tour_id: '',
      discount_percentage: '',
      valid_from: '',
      valid_to: '',
      is_active: true,
      terms_conditions: '',
    })
    setShowModal(true)
  }

  const handleEditOffer = (offer) => {
    setModalType('offer')
    setEditingItem(offer)
    setOfferForm({
      title: offer.title,
      description: offer.description,
      offer_type: offer.offer_type,
      tour_id: offer.tour_id || '',
      discount_percentage: offer.discount_percentage,
      valid_from: offer.valid_from,
      valid_to: offer.valid_to,
      is_active: offer.is_active !== false,
      terms_conditions: offer.terms_conditions || '',
    })
    setShowModal(true)
  }

  const handleSaveOffer = async () => {
    try {
      const offerData = { ...offerForm }

      if (editingItem) {
        await adminService.updatePromotionalOffer(editingItem.id, offerData)
        toast.success('Offer updated successfully')
      } else {
        await adminService.createPromotionalOffer(offerData)
        toast.success('Offer created successfully')
      }

      setShowModal(false)
      loadData()
    } catch (error) {
      console.error('Error saving offer:', error)
      const message = error.response?.data?.message || error.message || 'Failed to save offer'
      toast.error(message)
    }
  }

  const handleDeleteOffer = async (id) => {
    if (window.confirm('Are you sure you want to delete this offer?')) {
      try {
        await adminService.deletePromotionalOffer(id)
        toast.success('Offer deleted successfully')
        loadData()
      } catch (error) {
        console.error('Error deleting offer:', error)
        toast.error('Failed to delete offer')
      }
    }
  }

  // Filter services by type
  const filteredServices = services.filter(
    (service) =>
      (serviceFilter === 'all' || service.type === serviceFilter) &&
      (service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.description?.toLowerCase().includes(searchTerm.toLowerCase())),
  )

  // Filter tours by search
  const filteredTours = tours.filter(
    (tour) =>
      tour.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tour.destination.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO title="Admin Dashboard" noindex />
      <ToastContainer />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-display font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Manage tours, services, and promotional offers</p>
        </div>

        {/* Tabs */}
        <div className="card mb-6">
          <div className="flex flex-wrap gap-2 p-4 border-b">
            <Button
              variant={activeTab === 'overview' ? 'tab-active' : 'tab-inactive'}
              icon={<FaChartBar />}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </Button>
            <Button
              variant={activeTab === 'tours' ? 'tab-active' : 'tab-inactive'}
              onClick={() => setActiveTab('tours')}
            >
              Tours & Packages
            </Button>
            <Button
              variant={activeTab === 'services' ? 'tab-active' : 'tab-inactive'}
              onClick={() => setActiveTab('services')}
            >
              Services (Cars, Hotels, Guides)
            </Button>
            <Button
              variant={activeTab === 'discounts' ? 'tab-active' : 'tab-inactive'}
              icon={<FaPercent />}
              onClick={() => setActiveTab('discounts')}
            >
              Discount Codes
            </Button>
            <Button
              variant={activeTab === 'offers' ? 'tab-active' : 'tab-inactive'}
              icon={<FaTags />}
              onClick={() => setActiveTab('offers')}
            >
              Promotional Offers
            </Button>
            <Button
              variant={activeTab === 'queries' ? 'tab-active' : 'tab-inactive'}
              icon={<FaEnvelope />}
              onClick={() => {
                setActiveTab('queries')
                setQueryFilter('all')
              }}
            >
              Customer Queries
              {queryStats.pending > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {queryStats.pending}
                </span>
              )}
            </Button>
            <Button
              variant={activeTab === 'bookings' ? 'tab-active' : 'tab-inactive'}
              icon={<FaCalendarCheck />}
              onClick={() => {
                setActiveTab('bookings')
                setBookingFilter('all')
              }}
            >
              Bookings
              {bookings.filter((b) => b.status === 'pending').length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-full">
                  {bookings.filter((b) => b.status === 'pending').length}
                </span>
              )}
            </Button>
            <Button
              variant={activeTab === 'custom-trips' ? 'tab-active' : 'tab-inactive'}
              icon={<FaRoute />}
              onClick={() => setActiveTab('custom-trips')}
            >
              Custom Trips
            </Button>
            <Button
              variant={activeTab === 'reviews' ? 'tab-active' : 'tab-inactive'}
              icon={<FaComments />}
              onClick={() => {
                setActiveTab('reviews')
                fetchReviews()
              }}
            >
              Reviews
              {reviews.filter((r) => r.status === 'pending').length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full">
                  {reviews.filter((r) => r.status === 'pending').length}
                </span>
              )}
            </Button>
            <Button
              variant={activeTab === 'hero' ? 'tab-active' : 'tab-inactive'}
              icon={<FaImage />}
              onClick={() => setActiveTab('hero')}
            >
              Hero Slider
            </Button>
            <Button
              variant={activeTab === 'theme' ? 'tab-active' : 'tab-inactive'}
              icon={<FaPalette />}
              onClick={() => setActiveTab('theme')}
            >
              Theme
            </Button>
            <Button
              variant={activeTab === 'settings' ? 'tab-active' : 'tab-inactive'}
              icon={<FaCog />}
              onClick={() => {
                setActiveTab('settings')
                setSettingsFilter('all')
              }}
            >
              Site Settings
            </Button>
            <Button
              variant={activeTab === 'locations' ? 'tab-active' : 'tab-inactive'}
              icon={<FaMapMarkerAlt />}
              onClick={() => setActiveTab('locations')}
            >
              Locations
            </Button>
            <Button
              variant={activeTab === 'about' ? 'tab-active' : 'tab-inactive'}
              icon={<FaUsers />}
              onClick={() => setActiveTab('about')}
            >
              About &amp; Team
            </Button>
            <Button
              variant={activeTab === 'policies' ? 'tab-active' : 'tab-inactive'}
              icon={<FaShieldAlt />}
              onClick={() => setActiveTab('policies')}
            >
              Policies
            </Button>
            <Button
              variant={activeTab === 'emailTemplates' ? 'tab-active' : 'tab-inactive'}
              icon={<FaEnvelope />}
              onClick={() => setActiveTab('emailTemplates')}
            >
              Email Templates
            </Button>
            <Button
              variant={activeTab === 'bugs' ? 'tab-active' : 'tab-inactive'}
              icon={<FaBug />}
              onClick={() => setActiveTab('bugs')}
            >
              Bug Reports
            </Button>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Total Tours</p>
                  <p className="text-3xl font-bold text-gray-900">{tours.length}</p>
                </div>
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🗺️</span>
                </div>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Total Services</p>
                  <p className="text-3xl font-bold text-gray-900">{services.length}</p>
                </div>
                <div className="w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🚗</span>
                </div>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Active Discounts</p>
                  <p className="text-3xl font-bold text-gray-900">{discounts.length}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <FaPercent className="text-green-600 text-xl" />
                </div>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Active Offers</p>
                  <p className="text-3xl font-bold text-gray-900">{offers.length}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <FaTags className="text-orange-600 text-xl" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tours Tab */}
        {activeTab === 'tours' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div className="flex-1 mr-4">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search tours..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-field !pl-10"
                  />
                </div>
              </div>
              <Button icon={<FaPlus />} onClick={handleCreateTour}>
                Add New Tour
              </Button>
            </div>

            {loading ? (
              <Loader />
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {filteredTours.map((tour) => (
                  <div key={tour.id} className="card p-6">
                    <div className="flex flex-col md:flex-row gap-4">
                      <img
                        src={
                          Array.isArray(tour.images)
                            ? tour.images[0]
                            : typeof tour.images === 'string'
                              ? JSON.parse(tour.images)[0]
                              : 'https://placehold.co/200?text=No+Image'
                        }
                        alt={tour.title}
                        className="w-full md:w-48 h-32 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">{tour.title}</h3>
                            <p className="text-gray-600">
                              {tour.destination} • {tour.duration} Days
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditTour(tour)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => handleDeleteTour(tour.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                        <p className="text-gray-700 mb-3 line-clamp-2">{tour.description}</p>
                        <div className="flex flex-wrap gap-2">
                          <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                            ₹{tour.base_price.toLocaleString()}
                          </span>
                          <span className="px-3 py-1 bg-gray-100 rounded-full text-sm capitalize">
                            {tour.category}
                          </span>
                          <span className="px-3 py-1 bg-gray-100 rounded-full text-sm capitalize">
                            {tour.difficulty}
                          </span>
                          {tour.is_featured && (
                            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold">
                              Featured
                            </span>
                          )}
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-semibold ${
                              tour.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {tour.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div>
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
              <div className="flex-1 w-full md:w-auto">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search services..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-field !pl-10 w-full"
                  />
                </div>
              </div>
              <div className="flex gap-2 w-full md:w-auto">
                <select
                  value={serviceFilter}
                  onChange={(e) => setServiceFilter(e.target.value)}
                  className="input-field"
                >
                  <option value="all">All Services</option>
                  <option value="car">Cars</option>
                  <option value="hotel">Hotels</option>
                  <option value="homestay">Homestays</option>
                  <option value="guide">Guides</option>
                  <option value="camp_site">Camp Sites</option>
                  <option value="activity">Activities</option>
                  <option value="pickup_drop">Pickup/Drop</option>
                </select>
                <Button icon={<FaPlus />} onClick={handleCreateService}>
                  Add Service
                </Button>
              </div>
            </div>

            {loading ? (
              <Loader />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredServices.map((service) => (
                  <div key={service.id} className="card p-4">
                    {getServiceImage(service) && (
                      <img
                        src={getServiceImage(service)}
                        alt={service.name}
                        className="w-full h-40 object-cover rounded-lg mb-3"
                      />
                    )}
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">{service.name}</h3>
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                          {service.description}
                        </p>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <button
                          onClick={() => handleEditService(service)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition text-sm"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDeleteService(service.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition text-sm"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      <span className="px-2 py-1 bg-primary-100 text-primary-800 rounded-full text-xs font-semibold uppercase">
                        {service.type === 'pickup_drop' ? 'Pickup/Drop' : service.type}
                      </span>
                      <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                        ₹{service.base_price}/{service.price_unit.replace('_', ' ')}
                      </span>
                      {service.capacity && (
                        <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                          Capacity: {service.capacity}
                        </span>
                      )}
                    </div>
                    {service.amenities && (
                      <div className="flex flex-wrap gap-1">
                        {(typeof service.amenities === 'string'
                          ? JSON.parse(service.amenities)
                          : service.amenities
                        )
                          .slice(0, 3)
                          .map((amenity, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-gray-50 text-gray-600 rounded text-xs"
                            >
                              {amenity}
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Discounts Tab */}
        {activeTab === 'discounts' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Discount Codes</h2>
              <Button icon={<FaPlus />} onClick={handleCreateDiscount}>
                Create Discount Code
              </Button>
            </div>

            {loading ? (
              <Loader />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {discounts.map((discount) => (
                  <div key={discount.id} className="card p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{discount.code}</h3>
                        <p className="text-gray-600">{discount.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditDiscount(discount)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDeleteDiscount(discount.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-gray-500">Type</p>
                        <p className="font-semibold capitalize">{discount.discount_type}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Value</p>
                        <p className="font-semibold">
                          {discount.discount_type === 'percentage'
                            ? `${discount.discount_value}%`
                            : `₹${discount.discount_value}`}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Min Amount</p>
                        <p className="font-semibold">₹{discount.min_booking_amount || 0}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Applicable To</p>
                        <p className="font-semibold capitalize">{discount.applicable_to}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          discount.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {discount.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {discount.usage_limit && (
                        <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                          Used: {discount.used_count || 0}/{discount.usage_limit}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Offers Tab */}
        {activeTab === 'offers' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Promotional Offers</h2>
              <Button icon={<FaPlus />} onClick={handleCreateOffer}>
                Create Offer
              </Button>
            </div>

            {loading ? (
              <Loader />
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {offers.map((offer) => (
                  <div key={offer.id} className="card p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{offer.title}</h3>
                        <p className="text-gray-600 mb-2">{offer.description}</p>
                        {offer.tour_title && (
                          <p className="text-sm text-gray-500">Related Tour: {offer.tour_title}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditOffer(offer)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDeleteOffer(offer.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-4 py-2 bg-green-500 text-white rounded-full font-bold text-lg">
                        {offer.discount_percentage}% OFF
                      </span>
                      <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                        {new Date(offer.valid_from).toLocaleDateString()} -{' '}
                        {new Date(offer.valid_to).toLocaleDateString()}
                      </span>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          offer.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {offer.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm capitalize">
                        {offer.offer_type}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Queries Tab */}
        {activeTab === 'queries' && (
          <div>
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Customer Queries</h2>
                <div className="flex gap-2">
                  <select
                    value={queryFilter}
                    onChange={(e) => setQueryFilter(e.target.value)}
                    className="input-field"
                  >
                    <option value="all">All Queries</option>
                    <option value="pending">Pending</option>
                    <option value="replied">Replied</option>
                    <option value="resolved">Resolved</option>
                  </select>
                  <select
                    value={queryTypeFilter}
                    onChange={(e) => setQueryTypeFilter(e.target.value)}
                    className="input-field"
                  >
                    <option value="all">All Types</option>
                    <option value="general">General</option>
                    <option value="quote_request">Quote Requests</option>
                    <option value="location_request">Location Requests</option>
                  </select>
                </div>
              </div>

              {/* Query Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
                <div className="card p-4">
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="text-2xl font-bold text-gray-900">{queryStats.total || 0}</p>
                </div>
                <div className="card p-4">
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-orange-600">{queryStats.pending || 0}</p>
                </div>
                <div className="card p-4">
                  <p className="text-sm text-gray-500">Replied</p>
                  <p className="text-2xl font-bold text-blue-600">{queryStats.replied || 0}</p>
                </div>
                <div className="card p-4">
                  <p className="text-sm text-gray-500">Resolved</p>
                  <p className="text-2xl font-bold text-green-600">{queryStats.resolved || 0}</p>
                </div>
                <div className="card p-4">
                  <p className="text-sm text-gray-500">Quote Requests</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {queryStats.quote_requests || 0}
                  </p>
                </div>
                <div className="card p-4">
                  <p className="text-sm text-gray-500">Today</p>
                  <p className="text-2xl font-bold text-primary-600">{queryStats.today || 0}</p>
                </div>
              </div>
            </div>

            {loading ? (
              <Loader />
            ) : queries.length === 0 ? (
              <div className="text-center py-12">
                <FaEnvelope className="text-6xl text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No queries found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {queries.map((query) => (
                  <div key={query.id} className="card p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-gray-900">{query.subject}</h3>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              query.status === 'pending'
                                ? 'bg-orange-100 text-orange-800'
                                : query.status === 'replied'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {query.status.toUpperCase()}
                          </span>
                          {query.query_type === 'quote_request' && (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                              QUOTE REQUEST
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                          <span>
                            <strong>From:</strong> {query.name}
                          </span>
                          <span>
                            <strong>Email:</strong> {query.email}
                          </span>
                          {query.mobile_number && (
                            <span>
                              <strong>Phone:</strong> {query.mobile_number}
                            </span>
                          )}
                          <span className="text-gray-400">
                            {new Date(query.created_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {query.status !== 'resolved' && (
                          <button
                            onClick={async () => {
                              if (
                                window.confirm(
                                  `Mark this query as ${query.status === 'replied' ? 'resolved' : 'resolved'}?`,
                                )
                              ) {
                                try {
                                  await contactService.updateQueryStatus(query.id, 'resolved')
                                  toast.success('Query marked as resolved')
                                  loadData()
                                } catch (error) {
                                  toast.error('Failed to update status')
                                }
                              }
                            }}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                            title="Mark as Resolved"
                          >
                            <FaCheckCircle />
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to delete this query?')) {
                              try {
                                await contactService.deleteQuery(query.id)
                                toast.success('Query deleted successfully')
                                loadData()
                              } catch (error) {
                                toast.error('Failed to delete query')
                              }
                            }
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <p className="text-gray-700 whitespace-pre-wrap">{query.message}</p>
                    </div>

                    {query.admin_reply && (
                      <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FaReply className="text-blue-600" />
                          <span className="font-semibold text-blue-900">Your Reply:</span>
                          <span className="text-sm text-gray-600">
                            {query.replied_at &&
                              `on ${new Date(query.replied_at).toLocaleString()}`}
                            {query.replied_by_name && ` by ${query.replied_by_name}`}
                          </span>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{query.admin_reply}</p>
                      </div>
                    )}

                    {/* Reply Form */}
                    {query.status === 'pending' && (
                      <div className="mt-4">
                        <label className="label">Reply to this query</label>
                        <textarea
                          value={editingItem?.id === query.id ? replyText : ''}
                          onFocus={() => {
                            setEditingItem(query)
                            setReplyText('')
                          }}
                          onChange={(e) => setReplyText(e.target.value)}
                          className="input-field mb-2"
                          rows="4"
                          placeholder="Type your reply here..."
                        />
                        {editingItem?.id === query.id && (
                          <div className="flex gap-2">
                            <Button
                              icon={<FaReply />}
                              onClick={async () => {
                                if (!replyText.trim()) {
                                  toast.error('Please enter a reply')
                                  return
                                }
                                try {
                                  await contactService.replyToQuery(query.id, {
                                    admin_reply: replyText,
                                  })
                                  toast.success(
                                    'Reply sent successfully! Email has been sent to the customer.',
                                  )
                                  setReplyText('')
                                  setEditingItem(null)
                                  loadData()
                                } catch (error) {
                                  toast.error(
                                    error.response?.data?.message || 'Failed to send reply',
                                  )
                                }
                              }}
                            >
                              Send Reply via Email
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={() => {
                                setReplyText('')
                                setEditingItem(null)
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div>
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Booking Management</h2>
                <div className="flex gap-2">
                  <select
                    value={bookingFilter}
                    onChange={(e) => setBookingFilter(e.target.value)}
                    className="input-field"
                  >
                    <option value="all">All Bookings</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {/* Booking Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="card p-4">
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="text-2xl font-bold text-gray-900">{bookings.length}</p>
                </div>
                <div className="card p-4">
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {bookings.filter((b) => b.status === 'pending').length}
                  </p>
                </div>
                <div className="card p-4">
                  <p className="text-sm text-gray-500">Confirmed</p>
                  <p className="text-2xl font-bold text-green-600">
                    {bookings.filter((b) => b.status === 'confirmed').length}
                  </p>
                </div>
                <div className="card p-4">
                  <p className="text-sm text-gray-500">Completed</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {bookings.filter((b) => b.status === 'completed').length}
                  </p>
                </div>
                <div className="card p-4">
                  <p className="text-sm text-gray-500">Cancelled</p>
                  <p className="text-2xl font-bold text-red-600">
                    {bookings.filter((b) => b.status === 'cancelled').length}
                  </p>
                </div>
              </div>
            </div>

            {loading ? (
              <Loader />
            ) : bookings.length === 0 ? (
              <div className="text-center py-12">
                <FaCalendarCheck className="text-6xl text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No bookings found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings
                  .filter((booking) => bookingFilter === 'all' || booking.status === bookingFilter)
                  .map((booking) => (
                    <div key={booking.id} className="card p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-gray-900">
                              Booking #{booking.id}
                            </h3>
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                booking.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : booking.status === 'confirmed'
                                    ? 'bg-green-100 text-green-800'
                                    : booking.status === 'completed'
                                      ? 'bg-blue-100 text-blue-800'
                                      : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {booking.status.toUpperCase()}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p>
                              <strong>Customer:</strong> {booking.user_name} ({booking.user_email})
                            </p>
                            <p>
                              <strong>Booked:</strong> {formatDate(booking.created_at)}
                            </p>
                            {booking.tour_title && (
                              <p>
                                <strong>Tour:</strong> {booking.tour_title}
                              </p>
                            )}
                            <p>
                              <strong>Start Date:</strong> {formatDate(booking.start_date)}
                            </p>
                            {booking.number_of_days && (
                              <p>
                                <strong>Duration:</strong> {booking.number_of_days} days
                              </p>
                            )}
                            {booking.number_of_persons && (
                              <p>
                                <strong>Persons:</strong> {booking.number_of_persons}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-primary-600">
                            {formatCurrency(booking.final_amount || booking.total_amount)}
                          </p>
                          {booking.discount_amount > 0 && (
                            <p className="text-sm text-gray-500">
                              Discount: -{formatCurrency(booking.discount_amount)}
                            </p>
                          )}
                          {booking.admin_discount && (
                            <p className="text-sm text-green-600">
                              Admin Discount: -{formatCurrency(booking.admin_discount)}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Services */}
                      {booking.services && booking.services.length > 0 && (
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                          <p className="font-semibold text-gray-700 mb-2">Services:</p>
                          <div className="flex flex-wrap gap-2">
                            {booking.services.map((service, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1 bg-white rounded-full text-sm border border-gray-200"
                              >
                                {service.type === 'car' && <FaCar className="inline mr-1" />}
                                {service.type === 'hotel' && <FaHotel className="inline mr-1" />}
                                {service.type === 'guide' && <FaUser className="inline mr-1" />}
                                {service.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Special Requests */}
                      {booking.special_requests && (
                        <div className="bg-blue-50 p-4 rounded-lg mb-4">
                          <p className="font-semibold text-blue-900 mb-1">Special Requests:</p>
                          <p className="text-gray-700 text-sm">{booking.special_requests}</p>
                        </div>
                      )}

                      {/* Service Remarks */}
                      {booking.service_remarks &&
                        typeof booking.service_remarks === 'object' &&
                        Object.keys(booking.service_remarks).length > 0 && (
                          <div className="bg-indigo-50 p-4 rounded-lg mb-4">
                            <p className="font-semibold text-indigo-900 mb-2">Service Remarks:</p>
                            <div className="space-y-1 text-sm text-gray-700">
                              {booking.service_remarks.car && (
                                <p>
                                  <span className="font-medium">Car:</span>{' '}
                                  {booking.service_remarks.car}
                                </p>
                              )}
                              {booking.service_remarks.guide && (
                                <p>
                                  <span className="font-medium">Guide:</span>{' '}
                                  {booking.service_remarks.guide}
                                </p>
                              )}
                              {booking.service_remarks.hotel && (
                                <p>
                                  <span className="font-medium">Hotel:</span>{' '}
                                  {booking.service_remarks.hotel}
                                </p>
                              )}
                              {booking.service_remarks.camp_site && (
                                <p>
                                  <span className="font-medium">Camp Site:</span>{' '}
                                  {booking.service_remarks.camp_site}
                                </p>
                              )}
                              {booking.service_remarks.activities?.map((a, i) => (
                                <p key={i}>
                                  <span className="font-medium">Activity #{a.service_id}:</span>{' '}
                                  {a.remarks}
                                </p>
                              ))}
                            </div>
                          </div>
                        )}

                      {/* Activities */}
                      {booking.activities && booking.activities.length > 0 && (
                        <div className="bg-orange-50 p-4 rounded-lg mb-4">
                          <p className="font-semibold text-orange-900 mb-2">Activities:</p>
                          <div className="flex flex-wrap gap-2">
                            {booking.activities.map((activity, idx) => (
                              <span
                                key={activity.id || idx}
                                className="px-3 py-1 bg-white rounded-full text-sm border border-orange-200"
                              >
                                🏔️ {activity.name}
                                {activity.participants > 1 && ` (×${activity.participants})`}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Admin Notes */}
                      {booking.admin_notes && (
                        <div className="bg-purple-50 p-4 rounded-lg mb-4 border-l-4 border-purple-500">
                          <p className="font-semibold text-purple-900 mb-1">Admin Notes:</p>
                          <p className="text-gray-700 text-sm">{booking.admin_notes}</p>
                        </div>
                      )}

                      {/* Cancellation Reason */}
                      {booking.cancellation_reason && (
                        <div className="bg-red-50 p-4 rounded-lg mb-4 border-l-4 border-red-500">
                          <p className="font-semibold text-red-900 mb-1">Cancellation Reason:</p>
                          <p className="text-gray-700 text-sm">{booking.cancellation_reason}</p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => {
                            setSelectedBooking(booking)
                            setModalType('booking-details')
                            setShowModal(true)
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                        >
                          <FaEdit /> View/Edit Details
                        </button>

                        {booking.status === 'confirmed' && (
                          <button
                            onClick={() => generateVoucher(booking)}
                            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition flex items-center gap-2"
                          >
                            <FaDownload /> Download Voucher
                          </button>
                        )}

                        {booking.status === 'pending' && (
                          <>
                            <button
                              onClick={async () => {
                                if (window.confirm('Confirm this booking?')) {
                                  try {
                                    await adminService.confirmBooking(booking.id)
                                    toast.success(
                                      'Booking confirmed! Confirmation email sent to customer.',
                                    )
                                    loadData()
                                  } catch (error) {
                                    toast.error('Failed to confirm booking')
                                  }
                                }
                              }}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                            >
                              <FaCheckCircle /> Confirm Booking
                            </button>
                            <button
                              onClick={() => {
                                setSelectedBooking(booking)
                                setAdminDiscountForm({
                                  discount_type: 'percentage',
                                  discount_value: '',
                                  notes: '',
                                })
                                setModalType('apply-discount')
                                setShowModal(true)
                              }}
                              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2"
                            >
                              <FaPercent /> Apply Discount
                            </button>
                          </>
                        )}

                        {(booking.status === 'pending' || booking.status === 'confirmed') && (
                          <button
                            onClick={async () => {
                              const reason = prompt('Enter cancellation reason:')
                              if (reason) {
                                try {
                                  await bookingService.adminCancelBooking(booking.id, reason)
                                  toast.success('Booking cancelled')
                                  loadData()
                                } catch (error) {
                                  toast.error('Failed to cancel booking')
                                }
                              }
                            }}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
                          >
                            <FaBan /> Cancel Booking
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Custom Trips Tab */}
        {activeTab === 'custom-trips' && <CustomTripRequests />}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div>
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Reviews Management</h2>
                <div className="flex gap-2">
                  <select
                    value={reviewFilter}
                    onChange={(e) => {
                      setReviewFilter(e.target.value)
                      loadData()
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <select
                    value={reviewTypeFilter}
                    onChange={(e) => {
                      setReviewTypeFilter(e.target.value)
                      loadData()
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Types</option>
                    <option value="tour">Tours</option>
                    <option value="car">Cars</option>
                    <option value="guide">Guides</option>
                  </select>
                </div>
              </div>

              {loading ? (
                <Loader />
              ) : reviews.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow">
                  <FaComments className="mx-auto text-gray-400 mb-4" size={48} />
                  <p className="text-gray-500 text-lg">No reviews found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} className="bg-white rounded-xl shadow-sm border p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {review.item_name || 'Unknown Item'}
                            </h3>
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {review.reviewable_type?.toUpperCase()}
                            </span>
                            <span
                              className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                review.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : review.status === 'approved'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {review.status?.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                            <span>👤 {review.user_name}</span>
                            <span>📧 {review.user_email}</span>
                            <span>📅 {formatDate(review.created_at)}</span>
                          </div>
                          <div className="flex items-center gap-1 mb-3">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <FaStar
                                key={star}
                                className={
                                  star <= review.rating ? 'text-yellow-400' : 'text-gray-300'
                                }
                                size={18}
                              />
                            ))}
                            <span className="ml-2 font-semibold text-gray-700">
                              {review.rating}/5
                            </span>
                          </div>
                          {review.comment && (
                            <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                              "{review.comment}"
                            </p>
                          )}
                          {review.admin_note && (
                            <div className="mt-3 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                              <p className="text-sm font-semibold text-blue-900 mb-1">
                                Admin Note:
                              </p>
                              <p className="text-sm text-blue-800">{review.admin_note}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {review.status === 'pending' && (
                        <div className="flex gap-2 pt-4 border-t">
                          <button
                            onClick={async () => {
                              try {
                                await reviewService.updateReviewStatus(review.id, 'approved')
                                toast.success('Review approved successfully')
                                loadData()
                              } catch (error) {
                                toast.error('Failed to approve review')
                              }
                            }}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                          >
                            <FaCheckCircle /> Approve
                          </button>
                          <button
                            onClick={async () => {
                              const note = prompt('Reason for rejection (optional):')
                              try {
                                await reviewService.updateReviewStatus(
                                  review.id,
                                  'rejected',
                                  note || '',
                                )
                                toast.success('Review rejected')
                                loadData()
                              } catch (error) {
                                toast.error('Failed to reject review')
                              }
                            }}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
                          >
                            <FaBan /> Reject
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this review?')) {
                                try {
                                  await reviewService.deleteReview(review.id)
                                  toast.success('Review deleted successfully')
                                  loadData()
                                } catch (error) {
                                  toast.error('Failed to delete review')
                                }
                              }
                            }}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition flex items-center gap-2"
                          >
                            <FaTrash /> Delete
                          </button>
                        </div>
                      )}

                      {review.status === 'approved' && (
                        <div className="flex gap-2 pt-4 border-t">
                          <button
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this review?')) {
                                try {
                                  await reviewService.deleteReview(review.id)
                                  toast.success('Review deleted successfully')
                                  loadData()
                                } catch (error) {
                                  toast.error('Failed to delete review')
                                }
                              }
                            }}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
                          >
                            <FaTrash /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Locations Tab */}
        {activeTab === 'locations' && <LocationManager />}

        {/* About & Team Tab */}
        {activeTab === 'about' && <AboutSettings />}

        {/* Cancellation + Terms Policies Tab */}
        {activeTab === 'policies' && <PolicySettings />}

        {/* Hero Slider Tab */}
        {activeTab === 'hero' && <HeroSliderSettings />}

        {/* Theme Tab */}
        {activeTab === 'theme' && <ThemeSettings />}

        {/* Email Templates Tab */}
        {activeTab === 'emailTemplates' && <EmailTemplateSettings />}

        {/* Bug Reports Tab */}
        {activeTab === 'bugs' && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Bug Reports</h2>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-4">
                  <p className="text-gray-500 text-sm">Open</p>
                  <p className="text-2xl font-bold text-orange-600">{bugStats.open}</p>
                </div>
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-4">
                  <p className="text-gray-500 text-sm">Closed</p>
                  <p className="text-2xl font-bold text-green-600">{bugStats.closed}</p>
                </div>
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-4">
                  <p className="text-gray-500 text-sm">Total</p>
                  <p className="text-2xl font-bold text-gray-900">{bugStats.total}</p>
                </div>
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-4">
                  <p className="text-gray-500 text-sm">Critical</p>
                  <p className="text-2xl font-bold text-red-600">{bugStats.critical}</p>
                </div>
              </div>

              {/* Bug Report Form */}
              <BugReportForm
                onSuccess={() => loadData()}
                getActivityLog={getActivityLog}
                getEnvironment={getEnvironment}
              />

              {/* Filters */}
              <div className="flex gap-4 mt-6 mb-4">
                <select
                  value={bugFilter}
                  onChange={(e) => setBugFilter(e.target.value)}
                  className="input-field"
                >
                  <option value="all">All Status</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
                <select
                  value={bugSeverityFilter}
                  onChange={(e) => setBugSeverityFilter(e.target.value)}
                  className="input-field"
                >
                  <option value="all">All Severity</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              {/* Bug List */}
              {bugReports.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No bug reports found.</p>
              ) : (
                <div className="space-y-3">
                  {bugReports.map((bug) => {
                    const severityColors = {
                      critical: 'bg-red-100 text-red-800',
                      high: 'bg-orange-100 text-orange-800',
                      medium: 'bg-yellow-100 text-yellow-800',
                      low: 'bg-blue-100 text-blue-800',
                    }
                    const severity =
                      (bug.labels || [])
                        .find((l) => l.startsWith('severity:'))
                        ?.replace('severity:', '') || 'medium'
                    return (
                      <div
                        key={bug.number}
                        className="bg-white/80 backdrop-blur-lg rounded-xl shadow border border-white/30 p-4 flex items-center justify-between"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${severityColors[severity] || 'bg-gray-100 text-gray-800'}`}
                            >
                              {severity}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${bug.state === 'open' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
                            >
                              {bug.state}
                            </span>
                          </div>
                          <h4 className="font-medium text-gray-900 truncate">{bug.title}</h4>
                          <p className="text-xs text-gray-500 mt-1">
                            #{bug.number} · {new Date(bug.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <a
                          href={bug.html_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-3 p-2 text-gray-400 hover:text-primary-600 transition-colors"
                          title="View on GitHub"
                        >
                          <FaExternalLinkAlt />
                        </a>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div>
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Site Settings</h2>
                <div className="flex gap-2">
                  <select
                    value={settingsFilter}
                    onChange={(e) => {
                      setSettingsFilter(e.target.value)
                      loadData()
                    }}
                    className="input-field"
                  >
                    <option value="all">All Categories</option>
                    <option value="general">General</option>
                    <option value="contact">Contact Info</option>
                    <option value="social">Social Media</option>
                    <option value="footer">Footer</option>
                    <option value="email">Email</option>
                  </select>
                </div>
              </div>
            </div>

            {loading ? (
              <Loader />
            ) : siteSettings.length === 0 ? (
              <div className="text-center py-12">
                <FaCog className="text-6xl text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No settings found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {siteSettings.map((setting) => (
                  <div key={setting.id} className="card p-6">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-lg font-bold text-gray-900">{setting.setting_key}</h3>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              setting.category === 'general'
                                ? 'bg-blue-100 text-blue-800'
                                : setting.category === 'contact'
                                  ? 'bg-green-100 text-green-800'
                                  : setting.category === 'social'
                                    ? 'bg-purple-100 text-purple-800'
                                    : setting.category === 'footer'
                                      ? 'bg-orange-100 text-orange-800'
                                      : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {setting.category}
                          </span>
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              setting.is_public
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {setting.is_public ? 'Public' : 'Private'}
                          </span>
                        </div>
                        {setting.description && (
                          <p className="text-sm text-gray-500 mb-2">{setting.description}</p>
                        )}
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg mb-3">
                      {setting.setting_type === 'json' ? (
                        <div>
                          <p className="text-xs text-gray-500 mb-2">Type: Array/JSON</p>
                          <textarea
                            value={JSON.stringify(setting.setting_value, null, 2)}
                            onChange={(e) => {
                              try {
                                const newValue = JSON.parse(e.target.value)
                                const updatedSettings = siteSettings.map((s) =>
                                  s.id === setting.id ? { ...s, setting_value: newValue } : s,
                                )
                                setSiteSettings(updatedSettings)
                              } catch (error) {
                                // Invalid JSON, ignore
                              }
                            }}
                            className="input-field font-mono text-sm"
                            rows="4"
                          />
                        </div>
                      ) : (
                        <div>
                          <p className="text-xs text-gray-500 mb-2">Type: {setting.setting_type}</p>
                          {setting.setting_type === 'boolean' ? (
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={
                                  setting.setting_value === '1' || setting.setting_value === 'true'
                                }
                                onChange={(e) => {
                                  const updatedSettings = siteSettings.map((s) =>
                                    s.id === setting.id
                                      ? { ...s, setting_value: e.target.checked ? '1' : '0' }
                                      : s,
                                  )
                                  setSiteSettings(updatedSettings)
                                }}
                                className="mr-2"
                              />
                              <span className="text-sm text-gray-700">Enabled</span>
                            </label>
                          ) : (
                            <textarea
                              value={setting.setting_value || ''}
                              onChange={(e) => {
                                const updatedSettings = siteSettings.map((s) =>
                                  s.id === setting.id ? { ...s, setting_value: e.target.value } : s,
                                )
                                setSiteSettings(updatedSettings)
                              }}
                              className="input-field"
                              rows={
                                setting.setting_value && setting.setting_value.length > 50
                                  ? '3'
                                  : '1'
                              }
                            />
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          try {
                            await settingsService.updateSetting(
                              setting.setting_key,
                              setting.setting_value,
                            )
                            toast.success('Setting updated successfully')
                            loadData()
                          } catch (error) {
                            toast.error('Failed to update setting')
                          }
                        }}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex items-center gap-2"
                      >
                        <FaSave /> Save
                      </button>
                      <button
                        onClick={async () => {
                          if (
                            window.confirm(
                              `Are you sure you want to delete the setting "${setting.setting_key}"?`,
                            )
                          ) {
                            try {
                              await settingsService.deleteSetting(setting.setting_key)
                              toast.success('Setting deleted successfully')
                              loadData()
                            } catch (error) {
                              toast.error('Failed to delete setting')
                            }
                          }
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>

                    {setting.updated_at && (
                      <p className="text-xs text-gray-400 mt-2">
                        Last updated: {new Date(setting.updated_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Save All Button */}
            {siteSettings.length > 0 && (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={async () => {
                    try {
                      const settingsObj = {}
                      siteSettings.forEach((setting) => {
                        settingsObj[setting.setting_key] = setting.setting_value
                      })
                      await settingsService.updateMultipleSettings(settingsObj)
                      toast.success('All settings updated successfully')
                      loadData()
                    } catch (error) {
                      toast.error('Failed to update settings')
                    }
                  }}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2 font-semibold"
                >
                  <FaSave /> Save All Changes
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal for Create/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold">
                {modalType === 'booking-details' && 'Booking Details'}
                {modalType === 'apply-discount' && 'Apply Admin Discount'}
                {!modalType.startsWith('booking') &&
                  !modalType.startsWith('apply') &&
                  `${editingItem ? 'Edit' : 'Create'} ${modalType === 'tour' ? 'Tour' : modalType === 'service' ? 'Service' : modalType === 'discount' ? 'Discount' : 'Offer'}`}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false)
                  setSelectedBooking(null)
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-6">
              {modalType === 'booking-details' && selectedBooking && (
                <BookingDetailsForm
                  booking={selectedBooking}
                  onSave={async (updatedData) => {
                    try {
                      await adminService.updateBookingDetails(selectedBooking.id, updatedData)
                      toast.success('Booking updated successfully')
                      setShowModal(false)
                      loadData()
                    } catch (error) {
                      toast.error('Failed to update booking')
                    }
                  }}
                />
              )}
              {modalType === 'apply-discount' && selectedBooking && (
                <AdminDiscountForm
                  booking={selectedBooking}
                  form={adminDiscountForm}
                  setForm={setAdminDiscountForm}
                  onApply={async () => {
                    try {
                      const baseAmount =
                        selectedBooking.final_amount || selectedBooking.total_amount
                      let discountAmount
                      if (adminDiscountForm.discount_type === 'percentage') {
                        discountAmount =
                          (parseFloat(baseAmount) *
                            (parseFloat(adminDiscountForm.discount_value) || 0)) /
                          100
                      } else {
                        discountAmount = parseFloat(adminDiscountForm.discount_value) || 0
                      }
                      await adminService.applyAdminDiscount(selectedBooking.id, {
                        admin_discount: discountAmount,
                        admin_notes: adminDiscountForm.notes || '',
                      })
                      toast.success('Discount applied successfully')
                      setShowModal(false)
                      loadData()
                    } catch (error) {
                      toast.error('Failed to apply discount')
                    }
                  }}
                />
              )}
              {modalType === 'tour' && (
                <TourForm form={tourForm} setForm={setTourForm} onSave={handleSaveTour} />
              )}
              {modalType === 'service' && (
                <ServiceForm
                  form={serviceForm}
                  setForm={setServiceForm}
                  onSave={handleSaveService}
                />
              )}
              {modalType === 'discount' && (
                <DiscountForm
                  form={discountForm}
                  setForm={setDiscountForm}
                  onSave={handleSaveDiscount}
                />
              )}
              {modalType === 'offer' && (
                <OfferForm
                  form={offerForm}
                  setForm={setOfferForm}
                  tours={tours}
                  onSave={handleSaveOffer}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Tour Form Component
const TOUR_TITLE_MAX_LENGTH = 255
const TOUR_DESTINATION_MAX_LENGTH = 255
const TOUR_IMAGE_URL_MAX_LENGTH = 500

const TourForm = ({ form, setForm, onSave }) => {
  const [locations, setLocations] = React.useState([])

  React.useEffect(() => {
    const loadLocations = async () => {
      try {
        const locationService = (await import('../services/locationService')).default
        const data = await locationService.getAllLocationsFlat()
        setLocations(data.locations || [])
      } catch (error) {
        console.error('Error loading locations:', error)
      }
    }
    loadLocations()
  }, [])

  const handleImageChange = (index, value) => {
    const newImages = [...form.images]
    newImages[index] = value
    setForm({ ...form, images: newImages })
  }

  const addImageField = () => {
    setForm({ ...form, images: [...form.images, ''] })
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="label">Title *</label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          maxLength={TOUR_TITLE_MAX_LENGTH}
          className="input-field"
          required
        />
      </div>

      <div>
        <label className="label">Description *</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="input-field"
          rows="4"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Destination *</label>
          <input
            type="text"
            value={form.destination}
            onChange={(e) => setForm({ ...form, destination: e.target.value })}
            maxLength={TOUR_DESTINATION_MAX_LENGTH}
            className="input-field"
            required
          />
        </div>

        <div>
          <label className="label">Duration (Days) *</label>
          <input
            type="number"
            value={form.duration}
            onChange={(e) => setForm({ ...form, duration: e.target.value })}
            className="input-field"
            required
          />
        </div>

        <div>
          <label className="label">Base Price (₹) *</label>
          <input
            type="number"
            value={form.base_price}
            onChange={(e) => setForm({ ...form, base_price: e.target.value })}
            className="input-field"
            required
          />
        </div>

        <div>
          <label className="label">Max Group Size *</label>
          <input
            type="number"
            value={form.max_group_size}
            onChange={(e) => setForm({ ...form, max_group_size: e.target.value })}
            className="input-field"
            required
          />
        </div>

        <div>
          <label className="label">Difficulty</label>
          <select
            value={form.difficulty}
            onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
            className="input-field"
          >
            <option value="easy">Easy</option>
            <option value="moderate">Moderate</option>
            <option value="hard">Hard</option>
          </select>
        </div>

        <div>
          <label className="label">Category</label>
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="input-field"
          >
            <option value="standard">Standard</option>
            <option value="premium">Premium</option>
            <option value="luxury">Luxury</option>
          </select>
        </div>

        <div>
          <label className="label">Location</label>
          <select
            value={form.location_id}
            onChange={(e) => setForm({ ...form, location_id: e.target.value || '' })}
            className="input-field"
          >
            <option value="">No location</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.parent_name ? `${loc.parent_name} → ${loc.name}` : loc.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="label">Images (URLs)</label>
        {form.images.map((image, index) => (
          <input
            key={index}
            type="text"
            value={image}
            onChange={(e) => handleImageChange(index, e.target.value)}
            maxLength={TOUR_IMAGE_URL_MAX_LENGTH}
            className="input-field mb-2"
            placeholder="https://example.com/image.jpg"
          />
        ))}
        <button
          type="button"
          onClick={addImageField}
          className="text-primary-600 hover:underline text-sm"
        >
          + Add another image
        </button>
      </div>

      <div>
        <label className="label">Itinerary (JSON Format)</label>
        <textarea
          value={form.itinerary || ''}
          onChange={(e) => setForm({ ...form, itinerary: e.target.value })}
          className="input-field font-mono text-sm"
          rows="10"
          placeholder={`[
  {
    "day": 1,
    "title": "Arrival Day",
    "activities": ["Airport pickup", "Hotel check-in", "Beach visit"],
    "meals": ["Dinner"],
    "accommodation": "Beach Resort"
  },
  {
    "day": 2,
    "title": "Sightseeing",
    "activities": ["City tour", "Local market"],
    "meals": ["Breakfast", "Lunch", "Dinner"],
    "accommodation": "Beach Resort"
  }
]`}
        />
        <p className="text-xs text-gray-500 mt-1">
          Enter itinerary as JSON array. Each day should have: day (number), title, activities
          (array), meals (array), accommodation (string)
        </p>
      </div>

      <div>
        <label className="label">Included Services</label>
        {(form.included_services || ['']).map((service, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              value={service}
              onChange={(e) => {
                const updated = [...form.included_services]
                updated[index] = e.target.value
                setForm({ ...form, included_services: updated })
              }}
              className="input-field flex-1"
              placeholder="e.g. Breakfast, Airport transfer"
            />
            {form.included_services.length > 1 && (
              <button
                type="button"
                onClick={() => {
                  const updated = form.included_services.filter((_, i) => i !== index)
                  setForm({ ...form, included_services: updated })
                }}
                className="text-red-500 hover:text-red-700 text-sm"
              >
                Remove
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={() => setForm({ ...form, included_services: [...form.included_services, ''] })}
          className="text-primary-600 hover:underline text-sm"
        >
          + Add another service
        </button>
      </div>

      <div>
        <label className="label">Available Activities</label>
        {(form.available_activities || ['']).map((activity, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              value={activity}
              onChange={(e) => {
                const updated = [...form.available_activities]
                updated[index] = e.target.value
                setForm({ ...form, available_activities: updated })
              }}
              className="input-field flex-1"
              placeholder="e.g. Trekking, River Rafting"
            />
            {form.available_activities.length > 1 && (
              <button
                type="button"
                onClick={() => {
                  const updated = form.available_activities.filter((_, i) => i !== index)
                  setForm({ ...form, available_activities: updated })
                }}
                className="text-red-500 hover:text-red-700 text-sm"
              >
                Remove
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            setForm({ ...form, available_activities: [...form.available_activities, ''] })
          }
          className="text-primary-600 hover:underline text-sm"
        >
          + Add another activity
        </button>
      </div>

      <div className="flex gap-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={form.is_featured}
            onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
            className="mr-2"
          />
          <span>Featured Tour</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className="mr-2"
          />
          <span>Active</span>
        </label>
      </div>

      <Button fullWidth icon={<FaSave />} onClick={onSave}>
        Save Tour
      </Button>
    </div>
  )
}

// Service Form Component
const SERVICE_NAME_MAX_LENGTH = 255
const SERVICE_IMAGE_URL_MAX_LENGTH = 500

const ServiceForm = ({ form, setForm, onSave }) => {
  const [locations, setLocations] = React.useState([])
  const serviceNameLength = form.name?.length || 0

  React.useEffect(() => {
    const loadLocations = async () => {
      try {
        const locationService = (await import('../services/locationService')).default
        const data = await locationService.getAllLocationsFlat()
        setLocations(data.locations || [])
      } catch (error) {
        console.error('Error loading locations:', error)
      }
    }
    loadLocations()
  }, [])

  const handleAmenityChange = (index, value) => {
    const newAmenities = [...form.amenities]
    newAmenities[index] = value
    setForm({ ...form, amenities: newAmenities })
  }

  const handleImageChange = (index, value) => {
    const newImages = [...(form.images || [''])]
    newImages[index] = value
    setForm({ ...form, images: newImages })
  }

  const addAmenityField = () => {
    setForm({ ...form, amenities: [...form.amenities, ''] })
  }

  const addImageField = () => {
    setForm({ ...form, images: [...(form.images || ['']), ''] })
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="label">Name *</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          maxLength={SERVICE_NAME_MAX_LENGTH}
          className="input-field"
          required
        />
        <p
          className={`mt-1 text-xs ${serviceNameLength >= SERVICE_NAME_MAX_LENGTH ? 'text-amber-600' : 'text-gray-500'}`}
        >
          {serviceNameLength}/{SERVICE_NAME_MAX_LENGTH} characters
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Type *</label>
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="input-field"
          >
            <option value="car">Car</option>
            <option value="hotel">Hotel</option>
            <option value="homestay">Homestay</option>
            <option value="guide">Guide</option>
            <option value="camp_site">Camp Site</option>
            <option value="activity">Activity</option>
            <option value="pickup_drop">Pickup/Drop</option>
          </select>
        </div>

        <div>
          <label className="label">Price Unit *</label>
          <select
            value={form.price_unit}
            onChange={(e) => setForm({ ...form, price_unit: e.target.value })}
            className="input-field"
          >
            <option value="per_day">Per Day</option>
            <option value="per_person">Per Person</option>
            <option value="per_trip">Per Trip</option>
            <option value="fixed">Fixed</option>
          </select>
        </div>

        <div>
          <label className="label">Base Price (₹) *</label>
          <input
            type="number"
            value={form.base_price}
            onChange={(e) => setForm({ ...form, base_price: e.target.value })}
            className="input-field"
            required
          />
        </div>

        <div>
          <label className="label">Capacity</label>
          <input
            type="number"
            value={form.capacity}
            onChange={(e) => setForm({ ...form, capacity: e.target.value })}
            className="input-field"
          />
        </div>

        <div>
          <label className="label">Package Price (₹)</label>
          <input
            type="number"
            value={form.package_price}
            onChange={(e) => setForm({ ...form, package_price: e.target.value })}
            className="input-field"
            placeholder="Override price for package bookings"
          />
        </div>

        <div>
          <label className="label">Location</label>
          <select
            value={form.location_id}
            onChange={(e) => setForm({ ...form, location_id: e.target.value || '' })}
            className="input-field"
          >
            <option value="">No location</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="label">Description</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="input-field"
          rows="3"
        />
      </div>

      <div>
        <label className="label">Detailed Description</label>
        <textarea
          value={form.detailed_description}
          onChange={(e) => setForm({ ...form, detailed_description: e.target.value })}
          className="input-field"
          rows="5"
          placeholder="Add full details users can read on service detail page"
        />
      </div>

      <div>
        <label className="label">Image URL</label>
        <input
          type="text"
          value={form.image_url}
          onChange={(e) => setForm({ ...form, image_url: e.target.value })}
          maxLength={SERVICE_IMAGE_URL_MAX_LENGTH}
          className="input-field"
          placeholder="https://example.com/image.jpg"
        />
      </div>

      <div>
        <label className="label">Gallery Images (Multiple URLs)</label>
        {(form.images || ['']).map((image, index) => (
          <input
            key={index}
            type="text"
            value={image}
            onChange={(e) => handleImageChange(index, e.target.value)}
            maxLength={SERVICE_IMAGE_URL_MAX_LENGTH}
            className="input-field mb-2"
            placeholder="https://example.com/gallery-image.jpg"
          />
        ))}
        <button
          type="button"
          onClick={addImageField}
          className="text-primary-600 hover:underline text-sm"
        >
          + Add another image
        </button>
      </div>

      <div>
        <label className="label">Amenities / Features</label>
        {form.amenities.map((amenity, index) => (
          <input
            key={index}
            type="text"
            value={amenity}
            onChange={(e) => handleAmenityChange(index, e.target.value)}
            className="input-field mb-2"
            placeholder="e.g., AC, WiFi, GPS"
          />
        ))}
        <button
          type="button"
          onClick={addAmenityField}
          className="text-primary-600 hover:underline text-sm"
        >
          + Add another amenity
        </button>
      </div>

      <label className="flex items-center">
        <input
          type="checkbox"
          checked={form.is_active}
          onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          className="mr-2"
        />
        <span>Active</span>
      </label>

      <Button fullWidth icon={<FaSave />} onClick={onSave}>
        Save Service
      </Button>
    </div>
  )
}

// Discount Form Component
const DISCOUNT_CODE_MAX_LENGTH = 50

const DiscountForm = ({ form, setForm, onSave }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="label">Discount Code *</label>
        <input
          type="text"
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
          maxLength={DISCOUNT_CODE_MAX_LENGTH}
          className="input-field"
          placeholder="e.g., SUMMER2024"
          required
        />
      </div>

      <div>
        <label className="label">Description *</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="input-field"
          rows="2"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Discount Type *</label>
          <select
            value={form.discount_type}
            onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
            className="input-field"
          >
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed Amount</option>
          </select>
        </div>

        <div>
          <label className="label">Discount Value *</label>
          <input
            type="number"
            value={form.discount_value}
            onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
            className="input-field"
            placeholder={form.discount_type === 'percentage' ? 'e.g., 10' : 'e.g., 500'}
            required
          />
        </div>

        <div>
          <label className="label">Min Booking Amount (₹)</label>
          <input
            type="number"
            value={form.min_booking_amount}
            onChange={(e) => setForm({ ...form, min_booking_amount: e.target.value })}
            className="input-field"
          />
        </div>

        <div>
          <label className="label">Max Discount Amount (₹)</label>
          <input
            type="number"
            value={form.max_discount_amount}
            onChange={(e) => setForm({ ...form, max_discount_amount: e.target.value })}
            className="input-field"
          />
        </div>

        <div>
          <label className="label">Applicable To *</label>
          <select
            value={form.applicable_to}
            onChange={(e) => setForm({ ...form, applicable_to: e.target.value })}
            className="input-field"
          >
            <option value="all">All</option>
            <option value="tour">Tours Only</option>
            <option value="service">Services Only</option>
            <option value="group">Group Bookings</option>
            <option value="corporate">Corporate</option>
          </select>
        </div>

        <div>
          <label className="label">Usage Limit</label>
          <input
            type="number"
            value={form.usage_limit}
            onChange={(e) => setForm({ ...form, usage_limit: e.target.value })}
            className="input-field"
            placeholder="Leave empty for unlimited"
          />
        </div>

        <div>
          <label className="label">Start Date</label>
          <input
            type="date"
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            className="input-field"
          />
        </div>

        <div>
          <label className="label">End Date</label>
          <input
            type="date"
            value={form.end_date}
            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            className="input-field"
          />
        </div>
      </div>

      <label className="flex items-center">
        <input
          type="checkbox"
          checked={form.is_active}
          onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          className="mr-2"
        />
        <span>Active</span>
      </label>

      <Button fullWidth icon={<FaSave />} onClick={onSave}>
        Save Discount
      </Button>
    </div>
  )
}

// Offer Form Component
const OFFER_TITLE_MAX_LENGTH = 255

const OfferForm = ({ form, setForm, tours, onSave }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="label">Offer Title *</label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          maxLength={OFFER_TITLE_MAX_LENGTH}
          className="input-field"
          required
        />
      </div>

      <div>
        <label className="label">Description *</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="input-field"
          rows="3"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Offer Type *</label>
          <select
            value={form.offer_type}
            onChange={(e) => setForm({ ...form, offer_type: e.target.value })}
            className="input-field"
          >
            <option value="discount">Discount</option>
            <option value="package">Package Deal</option>
            <option value="seasonal">Seasonal</option>
          </select>
        </div>

        <div>
          <label className="label">Related Tour (Optional)</label>
          <select
            value={form.tour_id}
            onChange={(e) => setForm({ ...form, tour_id: e.target.value })}
            className="input-field"
          >
            <option value="">All Tours</option>
            {tours.map((tour) => (
              <option key={tour.id} value={tour.id}>
                {tour.title}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Discount Percentage *</label>
          <input
            type="number"
            value={form.discount_percentage}
            onChange={(e) => setForm({ ...form, discount_percentage: e.target.value })}
            className="input-field"
            placeholder="e.g., 15"
            required
          />
        </div>

        <div className="col-span-2 grid grid-cols-2 gap-4">
          <div>
            <label className="label">Valid From *</label>
            <input
              type="date"
              value={form.valid_from}
              onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
              className="input-field"
              required
            />
          </div>

          <div>
            <label className="label">Valid To *</label>
            <input
              type="date"
              value={form.valid_to}
              onChange={(e) => setForm({ ...form, valid_to: e.target.value })}
              className="input-field"
              required
            />
          </div>
        </div>
      </div>

      <div>
        <label className="label">Terms & Conditions</label>
        <textarea
          value={form.terms_conditions}
          onChange={(e) => setForm({ ...form, terms_conditions: e.target.value })}
          className="input-field"
          rows="3"
        />
      </div>

      <label className="flex items-center">
        <input
          type="checkbox"
          checked={form.is_active}
          onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          className="mr-2"
        />
        <span>Active</span>
      </label>

      <Button fullWidth icon={<FaSave />} onClick={onSave}>
        Save Offer
      </Button>
    </div>
  )
}

// Booking Details Form Component
const BookingDetailsForm = ({ booking, onSave }) => {
  const [expandedPolicy, setExpandedPolicy] = useState(null)
  const [form, setForm] = useState({
    start_date: booking.start_date ? new Date(booking.start_date).toLocaleDateString('en-CA') : '',
    end_date: booking.end_date ? new Date(booking.end_date).toLocaleDateString('en-CA') : '',
    number_of_persons: booking.number_of_persons || '',
    number_of_days: booking.number_of_days || '',
    num_adults: booking.num_adults || '',
    num_children: booking.num_children || '',
    special_requests: booking.special_requests || '',
    admin_notes: booking.admin_notes || '',
  })

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-green-100 text-green-800',
    completed: 'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800',
  }

  const paymentStatusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-green-100 text-green-800',
    refunded: 'bg-red-100 text-red-800',
  }

  return (
    <div className="space-y-5">
      {/* Header: Booking ID, Status Badges, Type */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-bold text-gray-900">Booking #{booking.id}</h3>
        <div className="flex items-center gap-2">
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[booking.status] || 'bg-gray-100 text-gray-800'}`}
          >
            {(booking.status || '').toUpperCase()}
          </span>
          {booking.payment_status && (
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold ${paymentStatusColors[booking.payment_status] || 'bg-gray-100 text-gray-800'}`}
            >
              Payment: {(booking.payment_status || '').toUpperCase()}
            </span>
          )}
          {booking.booking_type && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
              {booking.booking_type.charAt(0).toUpperCase() + booking.booking_type.slice(1)}
            </span>
          )}
        </div>
      </div>

      {/* Customer Info Section */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <p className="text-xs uppercase tracking-wide font-semibold text-blue-700 mb-2">
          Customer Information
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
          <p>
            <FaUser className="inline mr-1 text-blue-500" /> <strong>Name:</strong>{' '}
            {booking.user_name || 'N/A'}
          </p>
          <p>
            <FaEnvelope className="inline mr-1 text-blue-500" /> <strong>Email:</strong>{' '}
            {booking.user_email || 'N/A'}
          </p>
          {booking.mobile_number && (
            <p>
              📱 <strong>Phone:</strong> {booking.mobile_number}
            </p>
          )}
          {booking.group_type && booking.group_type !== 'individual' && (
            <p>
              <FaUsers className="inline mr-1 text-blue-500" /> <strong>Group Type:</strong>{' '}
              {booking.group_type.charAt(0).toUpperCase() + booking.group_type.slice(1)}
            </p>
          )}
        </div>
      </div>

      {/* Tour / Destination Info */}
      {(booking.tour_title || booking.destination) && (
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-xs uppercase tracking-wide font-semibold text-green-700 mb-2">
            Tour Information
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
            {booking.tour_title && (
              <p>
                <FaRoute className="inline mr-1 text-green-500" /> <strong>Tour:</strong>{' '}
                {booking.tour_title}
              </p>
            )}
            {booking.destination && (
              <p>
                📍 <strong>Destination:</strong> {booking.destination}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Date & Duration Info */}
      <div className="bg-indigo-50 p-4 rounded-lg">
        <p className="text-xs uppercase tracking-wide font-semibold text-indigo-700 mb-2">
          Schedule
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm text-gray-700">
          {booking.start_date && (
            <p>
              <FaCalendarCheck className="inline mr-1 text-indigo-500" /> <strong>Start:</strong>{' '}
              {formatDate(booking.start_date)}
            </p>
          )}
          {booking.end_date && (
            <p>
              <FaCalendarCheck className="inline mr-1 text-indigo-500" /> <strong>End:</strong>{' '}
              {formatDate(booking.end_date)}
            </p>
          )}
          {booking.travel_date && (
            <p>
              <FaCalendarCheck className="inline mr-1 text-indigo-500" />{' '}
              <strong>Travel Date:</strong> {formatDate(booking.travel_date)}
            </p>
          )}
          {booking.number_of_days && (
            <p>
              <strong>Duration:</strong> {booking.number_of_days} day
              {booking.number_of_days > 1 ? 's' : ''}
            </p>
          )}
          {booking.number_of_persons && (
            <p>
              <FaUsers className="inline mr-1 text-indigo-500" /> <strong>Persons:</strong>{' '}
              {booking.number_of_persons}
            </p>
          )}
        </div>
      </div>

      {/* Services Attached */}
      {booking.services && booking.services.length > 0 && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-xs uppercase tracking-wide font-semibold text-gray-700 mb-2">
            Services
          </p>
          <div className="flex flex-wrap gap-2">
            {booking.services.map((service, idx) => (
              <span
                key={service.id || idx}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-white rounded-full text-sm border border-gray-200 shadow-sm"
              >
                {service.type === 'car' && <FaCar className="text-orange-500" />}
                {service.type === 'hotel' && <FaHotel className="text-purple-500" />}
                {service.type === 'guide' && <FaUser className="text-cyan-500" />}
                {service.name}
                {service.quantity > 1 && (
                  <span className="text-xs text-gray-400">×{service.quantity}</span>
                )}
                {service.price > 0 && (
                  <span className="text-xs text-gray-400">({formatCurrency(service.price)})</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Activities Attached */}
      {booking.activities && booking.activities.length > 0 && (
        <div className="bg-orange-50 p-4 rounded-lg">
          <p className="text-xs uppercase tracking-wide font-semibold text-orange-700 mb-2">
            Activities
          </p>
          <div className="flex flex-wrap gap-2">
            {booking.activities.map((activity, idx) => (
              <span
                key={activity.id || idx}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-white rounded-full text-sm border border-orange-200 shadow-sm"
              >
                🏔️ {activity.name}
                {activity.activity_type && (
                  <span className="text-xs text-gray-400">({activity.activity_type})</span>
                )}
                {activity.participants > 1 && (
                  <span className="text-xs text-gray-400">×{activity.participants} pax</span>
                )}
                {activity.price > 0 && (
                  <span className="text-xs text-gray-400">({formatCurrency(activity.price)})</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Pricing Section */}
      <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
        <p className="text-xs uppercase tracking-wide font-semibold text-emerald-700 mb-2">
          Pricing Summary
        </p>
        {booking.price_breakup ? (
          <PriceBreakup breakup={booking.price_breakup} />
        ) : (
          <div className="space-y-1 text-sm text-gray-700">
            <div className="flex justify-between">
              <span>Total Amount</span>
              <span className="font-medium">
                {formatCurrency(booking.total_amount || booking.total_price || 0)}
              </span>
            </div>
            {(booking.discount_amount > 0 || booking.coupon_code) && (
              <div className="flex justify-between text-orange-600">
                <span>
                  Coupon Discount{' '}
                  {booking.coupon_code && <span className="text-xs">({booking.coupon_code})</span>}
                </span>
                <span>-{formatCurrency(booking.discount_amount || 0)}</span>
              </div>
            )}
            {booking.admin_discount > 0 && (
              <div className="flex justify-between text-purple-600">
                <span>Admin Discount</span>
                <span>-{formatCurrency(booking.admin_discount)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-emerald-300 pt-2 mt-2 text-base font-bold text-emerald-800">
              <span>Final Amount</span>
              <span>{formatCurrency(booking.final_amount || booking.final_price || 0)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Guest Breakdown (read-only) */}
      {booking.num_adults || booking.num_children ? (
        <div className="bg-amber-50 p-4 rounded-lg">
          <p className="text-xs uppercase tracking-wide font-semibold text-amber-700 mb-2">
            Guest Breakdown
          </p>
          <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
            <p>
              <strong>Adults:</strong> {booking.num_adults || 0}
            </p>
            <p>
              <strong>Children:</strong> {booking.num_children || 0}
            </p>
          </div>
        </div>
      ) : null}

      {/* Guest Details */}
      {booking.guests && booking.guests.length > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-xs uppercase tracking-wide font-semibold text-blue-700 mb-2">
            Guest Details
          </p>
          <div className="space-y-2">
            {booking.guests.map((guest, idx) => (
              <div
                key={guest.id || idx}
                className="flex items-center gap-3 bg-white px-3 py-2 rounded-lg border border-blue-100 text-sm"
              >
                <span
                  className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${guest.is_child ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}
                >
                  {guest.is_child ? 'C' : 'A'}
                </span>
                <span className="font-medium text-gray-800">{guest.full_name}</span>
                <span className="text-gray-500">Age: {guest.age}</span>
                <span className="text-gray-500 capitalize">{guest.gender}</span>
                {guest.id_type && guest.id_type !== 'none' && (
                  <span className="text-gray-400 text-xs">
                    {guest.id_type.replace('_', ' ')} •••{guest.id_number?.slice(-4)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cancellation Reason (read-only) */}
      {booking.cancellation_reason && (
        <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
          <p className="text-xs uppercase tracking-wide font-semibold text-red-700 mb-1">
            Cancellation Reason
          </p>
          <p className="text-sm text-gray-700">{booking.cancellation_reason}</p>
        </div>
      )}

      {/* Policy Versions at Booking Time */}
      <div className="bg-purple-50 p-4 rounded-lg">
        <p className="text-xs uppercase tracking-wide font-semibold text-purple-700 mb-3">
          Policy Versions at Booking Time
        </p>
        <div className="space-y-2">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 w-24">Cancellation:</span>
              {booking.cancellation_policy ? (
                <button
                  type="button"
                  onClick={() =>
                    setExpandedPolicy(expandedPolicy === 'cancellation' ? null : 'cancellation')
                  }
                  className="text-sm text-purple-700 hover:text-purple-900 font-medium"
                >
                  v{booking.cancellation_policy.version}{' '}
                  {expandedPolicy === 'cancellation' ? '▲' : '▼'}
                </button>
              ) : (
                <span
                  className="text-sm text-gray-400"
                  title="Created before policy versioning was introduced"
                >
                  —
                </span>
              )}
            </div>
            {expandedPolicy === 'cancellation' && booking.cancellation_policy && (
              <div className="ml-26 bg-white border border-purple-100 rounded p-3 text-xs text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
                {booking.cancellation_policy.content}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 w-24">Terms:</span>
              {booking.terms ? (
                <button
                  type="button"
                  onClick={() => setExpandedPolicy(expandedPolicy === 'terms' ? null : 'terms')}
                  className="text-sm text-purple-700 hover:text-purple-900 font-medium"
                >
                  v{booking.terms.version} {expandedPolicy === 'terms' ? '▲' : '▼'}
                </button>
              ) : (
                <span
                  className="text-sm text-gray-400"
                  title="Created before policy versioning was introduced"
                >
                  —
                </span>
              )}
            </div>
            {expandedPolicy === 'terms' && booking.terms && (
              <div className="ml-26 bg-white border border-purple-100 rounded p-3 text-xs text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
                {booking.terms.content}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Timestamps */}
      <div className="text-xs text-gray-400 flex flex-wrap gap-4">
        {booking.created_at && <span>Booked: {formatDate(booking.created_at)}</span>}
        {booking.updated_at && <span>Last Updated: {formatDate(booking.updated_at)}</span>}
      </div>

      {/* Divider */}
      <hr className="border-gray-200" />

      {/* Editable Fields */}
      <p className="text-xs uppercase tracking-wide font-semibold text-gray-500">
        Edit Booking Details
      </p>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Start Date *</label>
          <input
            type="date"
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            className="input-field"
            required
          />
        </div>

        <div>
          <label className="label">End Date</label>
          <input
            type="date"
            value={form.end_date}
            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            className="input-field"
          />
        </div>

        <div>
          <label className="label">Number of Persons</label>
          <input
            type="number"
            value={form.number_of_persons}
            onChange={(e) => setForm({ ...form, number_of_persons: e.target.value })}
            className="input-field"
            min="1"
          />
        </div>

        <div>
          <label className="label">Number of Days</label>
          <input
            type="number"
            value={form.number_of_days}
            onChange={(e) => setForm({ ...form, number_of_days: e.target.value })}
            className="input-field"
            min="1"
          />
        </div>

        <div>
          <label className="label">Adults</label>
          <input
            type="number"
            value={form.num_adults}
            onChange={(e) => setForm({ ...form, num_adults: e.target.value })}
            className="input-field"
            min="0"
          />
        </div>

        <div>
          <label className="label">Children</label>
          <input
            type="number"
            value={form.num_children}
            onChange={(e) => setForm({ ...form, num_children: e.target.value })}
            className="input-field"
            min="0"
          />
        </div>
      </div>

      <div>
        <label className="label">Special Requests</label>
        <textarea
          value={form.special_requests}
          onChange={(e) => setForm({ ...form, special_requests: e.target.value })}
          className="input-field"
          rows="3"
          placeholder="Any special requests from customer..."
        />
      </div>

      <div>
        <label className="label">Admin Notes</label>
        <textarea
          value={form.admin_notes}
          onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
          className="input-field"
          rows="3"
          placeholder="Internal notes about this booking..."
        />
      </div>

      <Button fullWidth icon={<FaSave />} onClick={() => onSave(form)}>
        Save Changes
      </Button>
    </div>
  )
}

// Admin Discount Form Component
const AdminDiscountForm = ({ booking, form, setForm, onApply }) => {
  const calculateDiscountAmount = () => {
    const baseAmount = booking.final_amount || booking.total_amount
    if (form.discount_type === 'percentage') {
      return (baseAmount * (parseFloat(form.discount_value) || 0)) / 100
    } else {
      return parseFloat(form.discount_value) || 0
    }
  }

  const discountAmount = calculateDiscountAmount()
  const finalAmount = (booking.final_amount || booking.total_amount) - discountAmount

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 p-4 rounded-lg mb-4">
        <p className="text-sm text-gray-600">
          <strong>Customer:</strong> {booking.user_name}
        </p>
        <p className="text-sm text-gray-600">
          <strong>Current Amount:</strong>{' '}
          {formatCurrency(booking.final_amount || booking.total_amount)}
        </p>
        {booking.discount_amount > 0 && (
          <p className="text-sm text-gray-600">
            <strong>Coupon Discount:</strong> -{formatCurrency(booking.discount_amount)}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Discount Type *</label>
          <select
            value={form.discount_type}
            onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
            className="input-field"
            required
          >
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed Amount</option>
          </select>
        </div>

        <div>
          <label className="label">
            {form.discount_type === 'percentage' ? 'Percentage (%)' : 'Amount (₹)'} *
          </label>
          <input
            type="number"
            value={form.discount_value}
            onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
            className="input-field"
            placeholder={form.discount_type === 'percentage' ? 'e.g., 10' : 'e.g., 500'}
            min="0"
            step={form.discount_type === 'percentage' ? '0.01' : '1'}
            required
          />
        </div>
      </div>

      <div>
        <label className="label">Admin Notes</label>
        <textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="input-field"
          rows="2"
          placeholder="Reason for discount (optional)..."
        />
      </div>

      {form.discount_value && (
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="space-y-2">
            <p className="text-sm">
              <strong>Admin Discount:</strong> -{formatCurrency(discountAmount)}
            </p>
            <p className="text-lg font-bold text-green-700">
              Final Amount: {formatCurrency(finalAmount)}
            </p>
          </div>
        </div>
      )}

      <Button
        fullWidth
        icon={<FaPercent />}
        onClick={onApply}
        disabled={!form.discount_value || parseFloat(form.discount_value) <= 0}
      >
        Apply Discount
      </Button>
    </div>
  )
}

export default AdminDashboard
